import type { StatId } from '../race/types';

/**
 * The mini-game contract. Sessions are deterministic 30 Hz sub-sims:
 * (tier, seed, input tape) → score. The scene samples input into one
 * bitmask per tick and records the tape; `resolveTraining` carries the
 * tape back into the ranch sim, which re-runs the SAME pure session to
 * score it — the command stream alone replays a career, and a hostile
 * command can't invent XP.
 *
 * Input separation is a design rule: the race owns Space. Gallop = ↑ tap,
 * Trail = hold, Gates = single press, Pen = ←/→ steering.
 */

export const DRILL_TICK_S = 1 / 30;

// Input bitmask, one per tick.
export const MASK_PRESSED = 1;
export const MASK_HELD = 2;
export const MASK_LEFT = 4;
export const MASK_RIGHT = 8;
export const MASK_LEFT_PRESSED = 16;
export const MASK_RIGHT_PRESSED = 32;
export const INPUT_MASK = 63;

export function steerOf(mask: number): -1 | 0 | 1 {
  const left = (mask & MASK_LEFT) !== 0;
  const right = (mask & MASK_RIGHT) !== 0;
  if (left === right) return 0;
  return left ? -1 : 1;
}

export type MinigameId =
  | 'gallop'
  | 'furlongs'
  | 'poles'
  | 'trail'
  | 'hills'
  | 'tempo'
  | 'gates'
  | 'flyingStart'
  | 'gears'
  | 'pen'
  | 'desensitize'
  | 'trust';

export interface SessionScore {
  /** 0..1 skill result. */
  raw: number;
  /** Human line for the results card ("312 m · combo ×7"). */
  detail: string;
  /** The session ended in its failure state (score still counts). */
  failed: boolean;
}

export interface MinigameSession {
  /** Advance one 30 Hz tick with the tick's input mask. */
  step(mask: number): void;
  readonly finished: boolean;
  score(): SessionScore;
}

/** Trait-driven tweaks threaded into a session (e.g. penDrainMul). */
export type SessionMods = Record<string, number>;

export interface MinigameDef {
  id: MinigameId;
  name: string;
  stat: StatId;
  energyCost: number;
  create(tier: number, seed: number, mods?: SessionMods): MinigameSession;
}

export type TrainingGrade = 'rough' | 'bronze' | 'silver' | 'gold' | 'perfect';

export const MAX_SESSION_XP = 23;

export function gradeFor(raw: number): TrainingGrade {
  const score = Math.max(0, Math.min(1, raw));
  if (score < 0.25) return 'rough';
  if (score < 0.5) return 'bronze';
  if (score < 0.75) return 'silver';
  if (score < 0.9) return 'gold';
  return 'perfect';
}

export function gradeXp(grade: TrainingGrade): number {
  switch (grade) {
    case 'rough': return 2;
    case 'bronze': return 8;
    case 'silver': return 13;
    case 'gold': return 17;
    case 'perfect': return 20;
  }
}

/** Performance alone sets the band. Facilities raise caps and difficulty;
 * feed buys attempts. Quick Learner is the only XP multiplier. */
export function xpFor(raw: number, xpMult = 1, legacyXpMult?: number): number {
  const multiplier = legacyXpMult ?? xpMult;
  return Math.min(MAX_SESSION_XP, Math.round(gradeXp(gradeFor(raw)) * multiplier));
}

const HARD_STOP_TICKS = 30 * 120;

/** Score a tape. Ticks beyond the tape read as no input; sessions always
 * terminate on their own clocks (belt: hard stop). */
export function runSession(
  def: MinigameDef,
  tier: number,
  seed: number,
  tape: readonly number[],
  mods?: SessionMods,
): SessionScore {
  const session = def.create(tier, seed, mods);
  let tick = 0;
  while (!session.finished && tick < HARD_STOP_TICKS) {
    session.step(tape[tick] ?? 0);
    tick += 1;
  }
  return session.score();
}
